import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Search, Copy, Star, TrendingUp, Sparkles, RefreshCw, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useA4FPromptEnhancement } from '@/hooks/useA4FPromptEnhancement';
import SEO, { createBreadcrumbSchema } from '@/components/SEO';

interface PromptTemplate {
  id: string;
  category: string;
  title: string;
  prompt_text: string;
  description: string;
  tags: string[];
  usage_count: number;
  is_featured: boolean;
}

const PromptLibrary = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<PromptTemplate[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Prompt Enhancement State
  const [userPrompt, setUserPrompt] = useState('');
  const [showEnhancedDialog, setShowEnhancedDialog] = useState(false);
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [enhancedPrompt, setEnhancedPrompt] = useState('');
  const { enhancePrompt, isEnhancing } = useA4FPromptEnhancement();

  const categories = ['All', 'Marketing', 'Business', 'Art', 'Social Media', 'E-commerce', 'Real Estate', 'Food', 'Photography'];

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [selectedCategory, searchQuery, templates]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('prompt_templates')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('usage_count', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load prompt templates');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.title.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.prompt_text.toLowerCase().includes(query) ||
        t.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleCopyPrompt = async (template: PromptTemplate) => {
    try {
      await navigator.clipboard.writeText(template.prompt_text);
      toast.success('Prompt copied to clipboard!');

      // Increment usage count
      await supabase.rpc('increment_prompt_usage', { template_id: template.id });

      // Update local state
      setTemplates(templates.map(t =>
        t.id === template.id ? { ...t, usage_count: t.usage_count + 1 } : t
      ));
    } catch (error) {
      console.error('Error copying prompt:', error);
      toast.error('Failed to copy prompt');
    }
  };

  const handleUsePrompt = async (template: PromptTemplate) => {
    try {
      // Copy to clipboard
      await navigator.clipboard.writeText(template.prompt_text);
      
      // Store in localStorage to be picked up by the generation page
      localStorage.setItem('selectedPrompt', template.prompt_text);
      
      // Increment usage count
      await supabase.rpc('increment_prompt_usage', { template_id: template.id });
      
      // Update local state
      setTemplates(templates.map(t =>
        t.id === template.id ? { ...t, usage_count: t.usage_count + 1 } : t
      ));
      
      toast.success('Prompt copied! Redirecting to Try Generator...');
      
      // Redirect to try generator page
      setTimeout(() => {
        navigate('/try-generator');
      }, 500);
    } catch (error) {
      console.error('Error using prompt:', error);
      toast.error('Failed to use prompt');
    }
  };

  // Prompt Enhancement Handlers
  const handleEnhancePrompt = async () => {
    if (!userPrompt.trim()) {
      toast.error('Please enter a prompt to enhance');
      return;
    }
    
    const enhanced = await enhancePrompt(userPrompt);
    if (enhanced) {
      setOriginalPrompt(userPrompt);
      setEnhancedPrompt(enhanced);
      setShowEnhancedDialog(true);
    }
  };

  const handleAcceptEnhanced = () => {
    setUserPrompt(enhancedPrompt);
    setShowEnhancedDialog(false);
    toast.success('Enhanced prompt applied!');
  };

  const handleKeepOriginal = () => {
    setShowEnhancedDialog(false);
  };

  const handleRegenerateEnhanced = async () => {
    const newEnhanced = await enhancePrompt(originalPrompt);
    if (newEnhanced) {
      setEnhancedPrompt(newEnhanced);
    }
  };

  const handleUseEnhancedPrompt = () => {
    if (!userPrompt.trim()) {
      toast.error('Please enter a prompt first');
      return;
    }
    
    localStorage.setItem('selectedPrompt', userPrompt);
    toast.success('Prompt saved! Redirecting to Try Generator...');
    setTimeout(() => {
      navigate('/try-generator');
    }, 500);
  };

  // ItemList schema for rich results
  const promptLibrarySchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "AI Image Prompt Library",
    "description": "500+ curated prompts for AI image generation across categories like marketing, e-commerce, art, and social media.",
    "url": "https://thebulkimagegenerator.com/prompt-library",
    "numberOfItems": templates.length || 500,
    "itemListElement": templates.slice(0, 10).map((template, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": template.title,
      "description": template.description || template.prompt_text.substring(0, 100)
    }))
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      <SEO
        title="AI Prompt Library | 500+ Ready-to-Use Image Generation Prompts"
        description="Browse our curated library of 500+ professional AI image prompts. Find inspiration and copy-paste prompts for marketing, e-commerce, art, social media, and more."
        keywords="ai prompts library, image generation prompts, flux prompts, ai art prompts, text to image prompts, creative prompts, prompt templates"
        url="/prompt-library"
        jsonLd={[
          promptLibrarySchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Prompt Library', url: '/prompt-library' }
          ])
        ]}
      />
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
      </div>

      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-7xl relative z-10">
        {/* Header Section */}
        <div className="text-center mb-8 mt-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-8 h-8 text-blue-400" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              AI Prompt Library
            </h1>
          </div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Browse our curated collection of professional prompts. Copy and customize them for your perfect image generation.
          </p>
        </div>

        {/* Prompt Enhancement Section */}
        <Card className="mb-12 max-w-4xl mx-auto bg-slate-900/80 backdrop-blur-sm border-blue-500/30 shadow-lg shadow-blue-500/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Sparkles className="w-5 h-5 text-blue-400" />
              Create & Enhance Your Prompt
            </CardTitle>
            <CardDescription className="text-gray-300">
              Enter your prompt and let AI enhance it for professional, high-quality results
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Textarea
                placeholder="Enter your prompt here... (e.g., 'a beautiful sunset over mountains')"
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                className="min-h-[120px] text-base bg-slate-900/80 border-blue-500/30 text-white placeholder:text-gray-400 focus:bg-slate-900/90 focus:border-blue-500/50 resize-none"
              />
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleEnhancePrompt}
                disabled={isEnhancing || !userPrompt.trim()}
                className="flex-1 min-w-[180px] bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                {isEnhancing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enhancing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Enhance Prompt
                  </>
                )}
              </Button>
              
              <Button
                onClick={handleUseEnhancedPrompt}
                disabled={!userPrompt.trim()}
                variant="outline"
                className="flex-1 min-w-[180px] border-blue-500/50 bg-blue-900/20 text-white hover:bg-blue-900/30 hover:border-blue-500/70"
              >
                Use This Prompt
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8 max-w-4xl mx-auto">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
          <span className="text-gray-400 text-sm font-medium">OR BROWSE TEMPLATES</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        </div>

        {/* Search and Filter Section */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search prompts by keyword, tag, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15"
            />
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="transition-all"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-400 border-r-transparent"></div>
            <p className="mt-4 text-gray-300">Loading prompt templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-300 text-lg">No prompts found matching your criteria.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All');
              }}
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card key={template.id} className="hover:shadow-lg hover:shadow-blue-500/20 transition-all group relative overflow-hidden bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15">
                {template.is_featured && (
                  <div className="absolute top-3 right-3 z-10">
                    <Badge variant="default" className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                      <Star className="w-3 h-3 mr-1" />
                      Featured
                    </Badge>
                  </div>
                )}

                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1 text-white">{template.title}</CardTitle>
                      <Badge variant="secondary" className="text-xs bg-blue-500/20 text-blue-300 border-blue-500/30">
                        {template.category}
                      </Badge>
                    </div>
                  </div>
                  {template.description && (
                    <CardDescription className="mt-2 text-gray-300">{template.description}</CardDescription>
                  )}
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Prompt Preview */}
                  <div className="bg-black/30 rounded-lg p-3 text-sm text-gray-300 font-mono line-clamp-3 border border-white/10">
                    {template.prompt_text}
                  </div>

                  {/* Tags */}
                  {template.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {template.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs bg-purple-500/10 text-purple-300 border-purple-500/30">
                          #{tag}
                        </Badge>
                      ))}
                      {template.tags.length > 3 && (
                        <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-300 border-purple-500/30">
                          +{template.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Stats and Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-white/10">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <TrendingUp className="w-3 h-3" />
                      <span>{template.usage_count} uses</span>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopyPrompt(template)}
                        className="gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => handleUsePrompt(template)}
                      >
                        Use
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg p-8 border border-white/10 backdrop-blur-sm">
          <h2 className="text-2xl font-bold mb-4 text-white">Ready to Create Amazing Images?</h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Use these prompts in our Try Generator to create stunning, professional images in seconds.
          </p>
          <Button size="lg" asChild className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
            <a href="/try-generator">
              <Sparkles className="w-4 h-4 mr-2" />
              Try Generator Now
            </a>
          </Button>
        </div>
      </main>

      <Footer />

      {/* Enhanced Prompt Dialog */}
      <Dialog open={showEnhancedDialog} onOpenChange={setShowEnhancedDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 border-blue-500/30">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-blue-400 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Enhanced Prompt Ready
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Your prompt has been enhanced! Choose which version you'd like to use.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Original Prompt */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                Original Prompt
              </h3>
              <div className="p-4 bg-slate-800/50 border border-white/20 rounded-lg">
                <p className="text-gray-200 text-sm leading-relaxed">{originalPrompt}</p>
              </div>
            </div>

            {/* Enhanced Prompt */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-blue-400 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></span>
                Enhanced Prompt
              </h3>
              <div className="p-4 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-blue-600/20 border border-blue-500/30 rounded-lg">
                <p className="text-gray-100 text-sm leading-relaxed">{enhancedPrompt}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4">
              <Button
                onClick={handleAcceptEnhanced}
                className="flex-1 min-w-[120px] bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white border-0"
              >
                <Check className="w-4 h-4 mr-2" />
                Use Enhanced
              </Button>
              
              <Button
                onClick={handleKeepOriginal}
                variant="outline"
                className="flex-1 min-w-[120px] border-white/20 text-white hover:bg-white/10"
              >
                <X className="w-4 h-4 mr-2" />
                Keep Original
              </Button>
              
              <Button
                onClick={handleRegenerateEnhanced}
                disabled={isEnhancing}
                variant="outline"
                className="flex-1 min-w-[120px] border-blue-500/50 text-blue-400 hover:bg-blue-900/20"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isEnhancing ? 'animate-spin' : ''}`} />
                Regenerate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromptLibrary;
